/**
 * Ad platform click identifiers read from URL search params.
 * No client-side storage — fully ePrivacy compliant.
 *
 * Google Ads:
 * - gclid:   standard click ID (desktop / Android)
 * - gbraid:  privacy-preserving click ID (iOS 14+ web-to-web)
 * - wbraid:  privacy-preserving click ID (web-to-app)
 *
 * Meta (Facebook / Instagram):
 * - fbclid:  click ID appended to ad destination URLs
 *
 * Reddit:
 * - rdt_cid: click ID appended to ad destination URLs
 *
 * LinkedIn:
 * - li_fat_id: first-party ad tracking click ID
 */

export interface AdClickIds {
  gclid: string | null;
  gbraid: string | null;
  wbraid: string | null;
  fbclid: string | null;
  rdt_cid: string | null;
  li_fat_id: string | null;
}

/** Click IDs appended to app.wayflyer.com links for conversion attribution. */
export const APP_LINK_AD_CLICK_KEYS = [
  "gclid",
  "gbraid",
  "wbraid",
  "fbclid",
  "rdt_cid",
  "li_fat_id",
] as const satisfies readonly (keyof AdClickIds)[];

export type AppLinkAdClickKey = (typeof APP_LINK_AD_CLICK_KEYS)[number];

export function getAdClickIdsFromUrl(): AdClickIds {
  if (typeof window === "undefined") {
    return {
      gclid: null,
      gbraid: null,
      wbraid: null,
      fbclid: null,
      rdt_cid: null,
      li_fat_id: null,
    };
  }
  const params = new URLSearchParams(window.location.search);
  return {
    gclid: params.get("gclid"),
    gbraid: params.get("gbraid"),
    wbraid: params.get("wbraid"),
    fbclid: params.get("fbclid"),
    rdt_cid: params.get("rdt_cid"),
    li_fat_id: params.get("li_fat_id"),
  };
}

type PosthogPropertyReader = (key: string) => unknown;

/** Prefer landing-page URL params, then PostHog session properties. */
export function resolveAppLinkAdClickIds(
  urlClickIds: AdClickIds,
  getPosthogProperty?: PosthogPropertyReader
): Partial<Record<AppLinkAdClickKey, string>> {
  const clickIds: Partial<Record<AppLinkAdClickKey, string>> = {};

  for (const key of APP_LINK_AD_CLICK_KEYS) {
    const value =
      urlClickIds[key] ?? (getPosthogProperty?.(key) as string | undefined);
    if (value) {
      clickIds[key] = value;
    }
  }

  return clickIds;
}

export function appendAppLinkAdClickIdsToUrl(
  href: string,
  clickIds: Partial<Record<AppLinkAdClickKey, string>>
): string {
  const url = new URL(href);
  for (const [key, value] of Object.entries(clickIds)) {
    if (value) {
      url.searchParams.set(key, value);
    }
  }
  return url.toString();
}
